import { useEffect, useMemo, useState } from "react";
import { Input, Modal, ModalContent } from "@heroui/react";
import { K1PrimaryButton, K1TertiaryButton } from "@/components/ui/k1-button";
import type { K1Fund } from "../services/api";
import type { K1FundSelection } from "../services/api";

type K1FundsModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  funds: K1Fund[];
  selectedIds: number[];
  selection: K1FundSelection;
  onApply: (selection: K1FundSelection) => void;
  singleSelect?: boolean;
  hideAll?: boolean;
  showClearAction?: boolean;
};

export function K1FundsModal({
  isOpen,
  onOpenChange,
  funds,
  selectedIds,
  selection,
  onApply,
  singleSelect = false,
  hideAll = false,
  showClearAction = false,
}: K1FundsModalProps) {
  const [draft, setDraft] = useState<string[]>([]);
  const [search, setSearch] = useState("");

  const sortedFunds = useMemo(
    () =>
      [...funds].sort((a, b) =>
        a.fund_name.localeCompare(b.fund_name, undefined, {
          sensitivity: "base",
        }),
      ),
    [funds],
  );
  const allFundIds = useMemo(
    () => sortedFunds.map((f) => String(f.id ?? f.fund_id)),
    [sortedFunds],
  );

  const filteredFunds = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return sortedFunds;
    return sortedFunds.filter((f) => f.fund_name.toLowerCase().includes(q));
  }, [search, sortedFunds]);

  const visibleIds = useMemo(
    () => filteredFunds.map((f) => String(f.id ?? f.fund_id)),
    [filteredFunds],
  );

  useEffect(() => {
    if (!isOpen) return;
    setDraft(selection.mode === "all" ? allFundIds : selectedIds.map(String));
    setSearch("");
  }, [isOpen, selectedIds, selection.mode, allFundIds]);

  const allVisible =
    visibleIds.length > 0 && visibleIds.every((id) => draft.includes(id));
  const someVisible =
    visibleIds.some((id) => draft.includes(id)) && !allVisible;

  const toggleOne = (id: string) => {
    if (singleSelect) {
      setDraft((prev) => (prev[0] === id ? [] : [id]));
      return;
    }
    setDraft((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id],
    );
  };

  const toggleAll = () => {
    if (singleSelect) return;
    if (allVisible) {
      setDraft((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }
    setDraft((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const handleApply = () => {
    if (!hideAll && !singleSelect && draft.length === allFundIds.length) {
      onApply({ mode: "all" });
      onOpenChange(false);
      return;
    }
    onApply({ mode: "ids", ids: draft.map(Number) });
    onOpenChange(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      placement="center"
      backdrop="opaque"
      scrollBehavior="normal"
      classNames={{
        wrapper: "z-[1300] overflow-hidden p-4",
        backdrop: "z-[1290] bg-slate-900/25 backdrop-blur-[2px]",
        base: "max-h-[calc(100vh-2rem)] w-[min(620px,calc(100vw-2rem))] max-w-none overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_20px_40px_rgba(15,23,42,0.10),0_6px_16px_rgba(15,23,42,0.06)]",
        closeButton:
          "h-9 w-9 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700",
      }}
    >
      <ModalContent className="overflow-hidden">
        {(onClose) => (
          <div className="relative flex h-[min(700px,calc(100vh-2rem))] flex-col overflow-hidden bg-white">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-[2px] bg-blue-200/80" />

            <div className="shrink-0 border-b border-slate-100 px-6 py-4.5">
              <div className="text-base font-semibold text-slate-800">
                Select funds
              </div>
              <div className="mt-1 text-xs text-slate-500">
                <span className="font-medium text-slate-700">
                  {draft.length}
                </span>{" "}
                of {funds.length} selected
              </div>
            </div>

            <div className="shrink-0 border-b border-slate-100 px-6 py-4">
              <Input
                value={search}
                onValueChange={setSearch}
                placeholder="Search funds..."
                autoFocus
                size="sm"
                variant="bordered"
                classNames={{
                  inputWrapper:
                    "h-11 rounded-xl border border-slate-200 bg-slate-50/70 shadow-[inset_0_1px_2px_rgba(15,23,42,0.03)] data-[focus=true]:border-blue-300 data-[focus-visible=true]:ring-2 data-[focus-visible=true]:ring-blue-200",
                  input:
                    "text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none",
                }}
              />
            </div>

            {!hideAll && !singleSelect && (
              <button
                type="button"
                onClick={toggleAll}
                className={[
                  "shrink-0 flex items-center justify-between border-b border-slate-100 px-6 py-2.5 text-sm transition-colors",
                  allVisible
                    ? "bg-blue-50/70 text-slate-700"
                    : "bg-slate-50/60 text-slate-600 hover:bg-slate-100/60",
                ].join(" ")}
              >
                <span className="flex items-center gap-2.5">
                  <span
                    className={[
                      "flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 text-white transition-colors",
                      allVisible || someVisible
                        ? "border-blue-600 bg-blue-600"
                        : "border-slate-300 bg-white",
                    ].join(" ")}
                  >
                    {(allVisible || someVisible) && (
                      <span className="text-[10px] leading-none">-</span>
                    )}
                  </span>
                  <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                    All funds
                  </span>
                </span>
                <span className="text-[11px] font-medium text-slate-500">
                  {filteredFunds.length}
                </span>
              </button>
            )}

            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-4 py-3">
              {filteredFunds.length === 0 ? (
                <div className="py-10 text-center text-sm text-slate-500">
                  No funds match your search
                </div>
              ) : (
                <div className="flex flex-col gap-1.5 pb-2">
                  {filteredFunds.map((fund) => {
                    const id = String(fund.id ?? fund.fund_id);
                    const isSelected = draft.includes(id);
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => toggleOne(id)}
                        className={[
                          "group flex w-full items-start gap-3 rounded-xl px-3.5 py-2.5 text-left transition-colors",
                          isSelected
                            ? "bg-blue-50/80 ring-1 ring-inset ring-blue-200"
                            : "hover:bg-slate-50",
                        ].join(" ")}
                      >
                        <span
                          className={[
                            "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-[8px] border text-[11px] font-semibold transition-colors",
                            isSelected
                              ? "border-blue-600 bg-blue-600 text-white"
                              : "border-slate-300 bg-white text-transparent",
                          ].join(" ")}
                        >
                          ✓
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium leading-5 text-slate-800">
                            {fund.fund_name}
                          </span>
                          <span className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                            <span>
                              {fund.latest_reporting_period ??
                                (fund.vintage_year
                                  ? `Vintage ${fund.vintage_year}`
                                  : "—")}
                            </span>
                            {fund.manager && (
                              <>
                                <span className="text-slate-300">•</span>
                                <span className="truncate">{fund.manager}</span>
                              </>
                            )}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="shrink-0 flex items-center justify-between border-t border-slate-100 bg-white px-6 py-4">
              {showClearAction ? (
                <K1TertiaryButton
                  size="sm"
                  className="h-10 rounded-md px-3"
                  onPress={() => {
                    onApply({ mode: "ids", ids: [] });
                    onOpenChange(false);
                  }}
                >
                  Clear fund
                </K1TertiaryButton>
              ) : (
                <span />
              )}
              <K1TertiaryButton
                size="sm"
                className="h-10 rounded-md px-3"
                onPress={onClose}
              >
                Cancel
              </K1TertiaryButton>
              <K1PrimaryButton
                size="sm"
                className="h-10 rounded-full px-5 shadow-[0_1px_0_rgba(255,255,255,0.12)_inset,0_4px_12px_rgba(37,99,235,0.24)]"
                onPress={handleApply}
                isDisabled={draft.length === 0}
              >
                Apply{draft.length > 0 ? ` (${draft.length})` : ""}
              </K1PrimaryButton>
            </div>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
}
